<script setup lang="ts">
import { useExplorerStore } from "@/modules/explorer/store";
import { useDocumentHistory } from "@/modules/explorer/documentHistoryStore";
import { computed, ref, watch, onBeforeMount, Ref } from "vue";
import DateComparison from "@/modules/explorer/components/DateSelections/DateComparison.vue";
import DateChip from "@/ui/components/date/DateChip.vue";
import SnapshotSelectionWrapper from "@/modules/explorer/components/SnapshotSelectionWrapper.vue";
import UnderlyingDataDialog from "@/modules/explorer/components/UnderlyingDataDialog.vue";
import PrimarySaveDocumentsDialog from "@/modules/explorer/components/ManageDocuments/PrimaryDialog.vue";
import OverwriteConfirmation from "@/modules/explorer/components/ManageDocuments/OverwriteConfirmation.vue";
import { useBaseStore } from "@/stores/base";
import useDialog from "@/ui/composables/useDialog";
import PeriodSelectorUI from "@/modules/explorer/components/PeriodFilterSelector/PeriodSelector.vue";
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import { useApiClient } from "@/api/client";
import BookmarkDO = App.Data.BookmarkDO;
import { convertDates } from "chartjs-graph";
import ToolbarBookmarksDialog from "@/modules/explorer/components/ToolbarBookmarksDialog.vue";
import Live from "@/ui/components/buttons/Live.vue";
import Modal from "@/ui/components/modal.vue";
import { effectiveState } from "@/Utils/proxy/DerivedModelProxyHandler";
import { useRoute, useRouter } from "vue-router";
import { usePropertyStore } from "@/modules/explorer/PropertyStore";
import SegmentType = App.Enums.SegmentType;
import ShareLinkDialog from "@/modules/explorer/components/ShareLinkDialog.vue";
import { t } from "@/plugins/i18n";
import { pivotRowKey } from "@/modules/explorer/components/Metrics/MetricHelper";

const dialog = useDialog();
const route = useRoute();
const router = useRouter();

// Stores
const explorerStore = useExplorerStore();
const documentHistory = useDocumentHistory();
const propertyStore = usePropertyStore();

// Computed Properties
const isNextDisabled = computed(() => documentHistory.canGoForward === false);

const isPreviousDisabled = computed(() => documentHistory.canGoBack === false);

const periodSelection = computed({
	get() {
		return explorerStore.document.period_selection;
	},
	set(periodSelection) {
		explorerStore.document.period_selection = periodSelection;
	},
});

const capacityTypes = effectiveState(
	() => useExplorerStore().document,
	() => useExplorerStore().derivedDocument,
	"visible_capacity_types"
);

const hasCapacityType = (type: string) =>
	computed<boolean>({
		get: () => (capacityTypes.value ?? []).includes(type),
		set: (value) => {
			const current = capacityTypes.value ?? [];
			capacityTypes.value = value
				? [...current, type]
				: current.filter((t) => t !== type);
		},
	});

const isNormalCapacityVisible = hasCapacityType("normal");
const isProgrammaticCapacityVisible = hasCapacityType("programmatic");

const isReservationPredictionVisible = effectiveState(
	() => useExplorerStore().document,
	() => useExplorerStore().derivedDocument,
	"is_reservation_prediction_visible"
);

const isDriveUpPredictionVisible = effectiveState(
	() => useExplorerStore().document,
	() => useExplorerStore().derivedDocument,
	"is_drive_up_prediction_visible"
);

const isMinimumOcucpancyVisible = effectiveState(
	() => useExplorerStore().document,
	() => useExplorerStore().derivedDocument,
	"is_minimum_occupancy_visible"
);

const isMaximumOccupancyVisible = effectiveState(
	() => useExplorerStore().document,
	() => useExplorerStore().derivedDocument,
	"is_maximum_occupancy_visible"
);

const isCapacityAvailable = computed(() => {
	return (
		route?.name === "explorer.metricWrapper" ||
		(useExplorerStore().document.vertical.supports_capacity &&
			propertyStore.isDocumentCompatibleWithDataSource(
				useExplorerStore().document,
				useExplorerStore().document.vertical.corresponding_capacity_table
			))
	);
});

const arePredictionsAvailable = computed(() => {
	return (
		route?.name === "explorer.metricWrapper" ||
		useExplorerStore().document.vertical.supports_predictions
	);
});

const areMinMaxOccupancyLayersAvailable = computed(() => {
	return useExplorerStore().document.vertical.name_clean === "occupancyavg";
});

const hasPivotedDataTable = computed(() => {
	if (explorerStore.derivedDocument?.view_tab !== "table") {
		return false;
	}

	return explorerStore.document.data_tables.some(
		(dataTable) => pivotRowKey(dataTable) !== null
	);
});

const isMediaDomain = computed(() => {
	return (
		useBaseStore().currentEnvironment?.product_plan?.name_clean === "media"
	);
});

// Refs for UI state
const isToolbarConfigurationVisible = ref(false);
const isDateSelectionVisible = ref(false);
const isSnapShotSelectionVisible = ref(false);
const isYOYSelectionVisible = ref(false);
const showData = ref(false);
const dataType = ref<string | undefined>();
const isManageDocumentInSaveState = ref(false);
const isManageDocumentsVisible = ref(false);
const isOverwriteDocumentVisible = ref(false);
const isShareLinkDialogVisible = ref(false);
const bookmarks: Ref<BookmarkDO[]> = ref([]);
const isLoadingBookmarks = ref(true);
const isCreateSegmentDialogVisible = ref(false);
const selectedSegmentType = ref<SegmentType>("customer");

// Methods
const loadHomeDocument = () => {
	explorerStore.FETCH_HOME_DOCUMENT(explorerStore.document.uuid);
};

const saveDocument = (showOverwriteDialog = false) => {
	if (showOverwriteDialog) {
		isOverwriteDocumentVisible.value = true;
	} else {
		openManageDocumentDialogInSaveState();
	}
};

const openManageDocumentDialogInSaveState = () => {
	isManageDocumentInSaveState.value = true;
	isManageDocumentsVisible.value = true;
};

const permissionToViewUnderlyingData = () => {
	const document = explorerStore.document;
	return !!document.vertical.data_source.contains("tab_occ_prk_v");
};

const openManageDocumentDialogInOpenState = async () => {
	if (useExplorerStore().explorerState.dashboardRedirectUuid) {
		const choice = await dialog.confirm({
			title: "Cancel editing dashboard?",
			message:
				"You are currently editing a card from a dashboard. Do you want to cancel and open a different configuration?",
		});
		if (!choice) return;
	}

	isManageDocumentInSaveState.value = false;
	isManageDocumentsVisible.value = true;
};

const openToolbarConfiguration = () => {
	isToolbarConfigurationVisible.value = true;
};

const userCanCreateSegments = computed(() => {
	return useBaseStore().currentUser?.derivedPermissions.includes(
		"can_manage_segments"
	);
});

// HACK: Do ensure that we *close* the v-menu we need to see if we had any filter changes
// E.g: I select a filter, the filter gets selected! Menu should open!
// I now click some random other spot, this doesn't update the selected filter
// therefor we can now close the menu.
const hasFilterChanged = ref(false);

watch(
	() => useExplorerStore().explorerState.selectedFilter,
	() => {
		const filter: ActiveFilterDTO =
			useExplorerStore().explorerState.selectedFilter;
		if (filter?.dimension?.type === "datetime") {
			isDateSelectionVisible.value = true;
			hasFilterChanged.value = true;
		}
	}
);

const periodSelector = ref();
// useDetectOutsideClick(periodSelector, (e) => {
// 	nextTick(() => {
// 		const target = e.target as HTMLElement;
//
// 		// Hack: Any VCalendar element, we ignore.
// 		if (
// 			target.classList &&
// 			Array.from(target.classList).some((className) =>
// 				className.startsWith("vc-")
// 			)
// 		) {
// 			return;
// 		}
//
// 		if (hasFilterChanged.value === false) {
// 			isDateSelectionVisible.value = false;
// 		}
//
// 		hasFilterChanged.value = false;
// 	});
// });

const navigateToCreateSegment = () => {
	router.push({
		path: `/segments/configure/document/${explorerStore.document.uuid}`,
		query: { type: selectedSegmentType.value },
	});
	isCreateSegmentDialogVisible.value = false;
};

const fetchBookmarks = async () => {
	isLoadingBookmarks.value = true;
	const result = await useApiClient("explorer").getBookmarks();
	if (result.isOk()) {
		bookmarks.value = result.value;
	}
	isLoadingBookmarks.value = false;
};

const openBookmarkedConfiguration = (bookmark: BookmarkDO) => {
	const document = { ...bookmark.stored_document.activeDocument };

	document.parent_uuid = useExplorerStore().document.uuid;
	useExplorerStore().document = {
		...convertDates(document),
	};

	// When a bookmark is loaded we do not track changes ( so that user can overwrite )
	// Use-case for bookmarks is a quick entrance to a view
	useExplorerStore().explorerState.userAbilityForCurrentDocument = ["view"];
	useExplorerStore().explorerState.lastOpenedSavedDocumentDocumentUuid = null;
};

const tooltipMsg = computed(() => {
	return (
		"Select " + t("periodSelection.periodsubjectdate").toLowerCase() + " date"
	);
});

onBeforeMount(() => {
	fetchBookmarks();
});
</script>

<template>
	<!-- Create Segment Dialog -->
	<modal
		v-model:visible="isCreateSegmentDialogVisible"
		title="Create segment"
		icon="fa: fa-light fa-object-intersect"
		@submit="navigateToCreateSegment"
	>
		<v-col cols="12">
			<h6 class="text-h6 mb-3">Type:</h6>
			<v-select
				v-model="selectedSegmentType"
				hide-details="auto"
				variant="filled"
				:items="['customer', 'reservation']"
			/>
		</v-col>
	</modal>

	<!-- Underlying Data Dialog -->
	<underlying-data-dialog
		v-model:data-is-visible="showData"
		v-model:data-type="dataType"
		@close="showData = false"
	/>

	<toolbar-bookmarks-dialog
		v-if="isToolbarConfigurationVisible && !isLoadingBookmarks"
		v-model="isToolbarConfigurationVisible"
		:bookmarks="bookmarks"
		@update-bookmarks="fetchBookmarks()"
	/>

	<!-- Overwrite Confirmation Dialog -->
	<overwrite-confirmation
		v-if="isOverwriteDocumentVisible"
		@save-as-new="openManageDocumentDialogInSaveState"
		v-model="isOverwriteDocumentVisible"
	/>

	<!-- Manage Documents Dialog -->
	<primary-save-documents-dialog
		:is-save-state="isManageDocumentInSaveState"
		:quick-create="!!explorerStore.explorerState.dashboardRedirectUuid"
		v-if="isManageDocumentsVisible"
		v-model="isManageDocumentsVisible"
	/>

	<share-link-dialog
		v-if="isShareLinkDialogVisible"
		v-model="isShareLinkDialogVisible"
	/>

	<!-- Toolbar -->
	<div class="d-flex align-center">
		<div>
			<Live v-model="explorerStore.document.is_updated_in_background" />
		</div>

		<!-- Document Navigation Buttons -->
		<v-divider vertical class="mx-2 mt-2" />
		<div style="flex-shrink: 0">
			<v-btn
				v-tooltip="'Open home-document'"
				icon
				variant="flat"
				size="small"
				@click="loadHomeDocument"
				color="on-surface"
				data-testid="open-home-document"
			>
				<i class="fa-light fa-house fa-xl"></i>
			</v-btn>
			<v-btn
				v-tooltip="'Open previous'"
				icon
				variant="text"
				size="small"
				:disabled="isPreviousDisabled"
				@click="documentHistory.getPreviousDocument()"
				color="on-surface"
			>
				<i class="fal fa-arrow-left fa-xl"></i>
			</v-btn>
			<v-btn
				v-tooltip="'Open next'"
				icon
				variant="text"
				size="small"
				:disabled="isNextDisabled"
				@click="documentHistory.getNextDocument()"
				color="on-surface"
			>
				<i class="fal fa-arrow-right fa-xl"></i>
			</v-btn>
		</div>
		<v-divider vertical class="mx-2 mt-2" />

		<!--		render user-toolbar bookmarks 	-->
		<div class="d-flex align-center overflow-x-auto">
			<v-btn
				v-for="(bookmark, index) in bookmarks"
				:key="`${index}-${bookmark.icon}`"
				:icon="bookmark.icon"
				v-tooltip="bookmark.stored_document?.title"
				variant="text"
				size="small"
				@click="openBookmarkedConfiguration(bookmark)"
				color="on-surface"
			/>

			<v-btn
				v-tooltip="'Add custom buttons to quickly open a stored document'"
				icon="fal fa-plus"
				variant="text"
				size="small"
				@click="openToolbarConfiguration"
				color="on-surface"
			/>
		</div>

		<v-spacer />

		<!-- Date Selection Menu -->
		<v-menu
			v-model="isDateSelectionVisible"
			:close-on-content-click="false"
			location="bottom"
		>
			<template v-slot:activator="{ props }">
				<date-chip
					v-tooltip="tooltipMsg"
					v-bind="props"
					:input-value="explorerStore.document.period_selection"
					color="on-surface"
					style="flex-shrink: 0"
					data-testid="date-selection"
				/>
			</template>
			<div ref="periodSelector">
				<PeriodSelectorUI
					v-model="periodSelection"
					@apply="isDateSelectionVisible = false"
				/>
			</div>
		</v-menu>

		<!-- Snapshot Selection Menu -->
		<v-menu
			v-model="isSnapShotSelectionVisible"
			:close-on-content-click="false"
			location="bottom"
		>
			<template v-slot:activator="{ props }">
				<v-btn
					v-tooltip="'Select snapshot date'"
					v-bind="props"
					icon
					variant="text"
					size="small"
					class="ml-3"
					color="on-surface"
					data-testid="snapshot-selection"
				>
					<i class="fa-light fa-timeline-arrow fa-xl"></i>
				</v-btn>
			</template>
			<v-card min-width="200">
				<snapshot-selection-wrapper
					v-model="isSnapShotSelectionVisible"
					v-model:period_measurement="explorerStore.document.period_measurement"
					v-model:period_measurement_parsed="
						explorerStore.document.period_measurement_parsed
					"
					v-model:transaction_snapshot="
						explorerStore.document.transaction_snapshot
					"
					v-model:transaction_snapshot_parsed="
						explorerStore.document.transaction_snapshot_parsed
					"
					:show-transaction-snapshot="
						explorerStore.document.vertical.supports_transaction_snapshotting
					"
				/>
			</v-card>
		</v-menu>

		<!-- YOY Selection Menu -->
		<v-menu
			v-model="isYOYSelectionVisible"
			:close-on-content-click="false"
			location="bottom"
		>
			<template v-slot:activator="{ props }">
				<v-btn
					v-tooltip="'Add comparison selection'"
					v-bind="props"
					icon="mdi-compare"
					:disabled="
						explorerStore.explorerState.isDataLoading ||
						!explorerStore.derivedDocument?.date_comparison ||
						hasPivotedDataTable
					"
					variant="text"
					size="small"
					color="on-surface"
					data-testid="comparison-selection"
				/>
			</template>
			<v-card min-width="600">
				<date-comparison @close="isYOYSelectionVisible = false" />
			</v-card>
		</v-menu>

		<v-divider vertical class="ml-2 my-2" />
		<v-btn
			height="26"
			v-tooltip="'Toggle Capacity'"
			icon
			variant="text"
			size="small"
			color="on-surface"
			rounded="0"
			:active="isNormalCapacityVisible"
			@click="isNormalCapacityVisible = !isNormalCapacityVisible"
			:disabled="!isCapacityAvailable"
		>
			CAP
		</v-btn>
		<v-divider vertical class="my-2" />
		<v-btn
			v-if="isMediaDomain"
			height="26"
			v-tooltip="'Toggle Programmatic Capacity'"
			icon
			variant="text"
			size="small"
			color="on-surface"
			rounded="0"
			:active="isProgrammaticCapacityVisible"
			@click="isProgrammaticCapacityVisible = !isProgrammaticCapacityVisible"
			:disabled="!isCapacityAvailable"
		>
			PRG
		</v-btn>
		<v-divider v-if="isMediaDomain" vertical class="my-2" />
		<v-btn
			v-if="!isMediaDomain"
			height="26"
			v-tooltip="'Toggle Reservation Prediction'"
			icon
			variant="text"
			size="small"
			color="on-surface"
			rounded="0"
			:active="isReservationPredictionVisible"
			@click="isReservationPredictionVisible = !isReservationPredictionVisible"
			:disabled="!arePredictionsAvailable"
		>
			RES
		</v-btn>
		<v-divider v-if="!isMediaDomain" vertical class="my-2" />
		<v-btn
			v-if="!isMediaDomain"
			height="26"
			v-tooltip="'Toggle Drive-up Prediction'"
			icon
			variant="text"
			size="small"
			color="on-surface"
			rounded="0"
			:active="isDriveUpPredictionVisible"
			@click="isDriveUpPredictionVisible = !isDriveUpPredictionVisible"
			:disabled="!arePredictionsAvailable"
		>
			DRI
		</v-btn>
		<v-divider v-if="!isMediaDomain" vertical class="my-2" />
		<v-menu
			v-if="!isMediaDomain"
			location="bottom right"
			:close-on-content-click="false"
		>
			<template v-slot:activator="{ props }">
				<v-btn
					v-bind="props"
					height="26"
					v-tooltip="'Show More'"
					icon
					variant="text"
					size="small"
					color="on-surface"
					rounded="0"
				>
					<v-icon>fa: fa-light fa-angle-down</v-icon>
				</v-btn>
			</template>
			<v-card class="mt-2 px-2 py-1">
				<div class="d-flex">
					<div class="text-caption mt-1">More:</div>
					<v-divider vertical class="ml-2 my-2" />
					<v-btn
						height="26"
						v-tooltip="'Toggle Minimum Occupancy'"
						icon
						variant="text"
						size="small"
						color="on-surface"
						rounded="0"
						:active="isMinimumOcucpancyVisible"
						@click="isMinimumOcucpancyVisible = !isMinimumOcucpancyVisible"
						:disabled="!areMinMaxOccupancyLayersAvailable"
					>
						MIN
					</v-btn>
					<v-divider vertical class="my-2" />
					<v-btn
						height="26"
						v-tooltip="'Toggle Maximum Occupancy'"
						icon
						variant="text"
						size="small"
						color="on-surface"
						rounded="0"
						:active="isMaximumOccupancyVisible"
						@click="isMaximumOccupancyVisible = !isMaximumOccupancyVisible"
						:disabled="!areMinMaxOccupancyLayersAvailable"
					>
						MAX
					</v-btn>
					<v-divider vertical class="my-2" />
				</div>
			</v-card>
		</v-menu>
		<v-divider v-if="!isMediaDomain" vertical class="my-2 mr-2" />

		<!-- Download Button for graph/datatable, depending on which component is mounted (uses teleport) -->
		<div id="download-button" />

		<!-- Save Button -->
		<div>
			<v-badge
				color="error"
				offset-x="10"
				offset-y="10"
				dot
				v-if="explorerStore.isOpenedButUnsaved"
			>
				<v-btn
					v-tooltip="'Save changes'"
					icon="fa: fa-solid fa-floppy-disk"
					variant="text"
					size="small"
					@click="saveDocument(true)"
					color="on-surface"
					data-testid="explorer-save-document-changes"
				/>
			</v-badge>
			<v-btn
				v-tooltip="'Save configuration'"
				v-else
				icon="fa: fa-solid fa-floppy-disk"
				variant="text"
				size="small"
				@click="saveDocument(explorerStore.isSavedDocument)"
				color="on-surface"
				data-testid="explorer-save-document"
			/>
		</div>

		<!-- Open button		<-->
		<v-btn
			v-tooltip="'Open configuration'"
			icon="fa: fa-regular fa-folder-open"
			variant="text"
			size="small"
			@click="openManageDocumentDialogInOpenState"
			color="on-surface"
			data-testid="explorer-open-document"
		/>

		<!-- Options Menu -->
		<v-menu>
			<template v-slot:activator="{ props }">
				<v-btn
					v-bind="props"
					icon="fa-regular fa-ellipsis-vertical"
					variant="text"
					size="small"
					color="on-surface"
					data-testid="explorer-options-menu"
				/>
			</template>
			<v-list class="pa-1">
				<v-list-item
					v-if="permissionToViewUnderlyingData"
					class="font-weight-regular text-caption"
					@click="
						showData = true;
						dataType = 'underlying-data';
					"
				>
					<template v-slot:prepend>
						<i class="fa-duotone fa-binary mr-3"></i>
					</template>
					View underlying data
				</v-list-item>
				<v-list-item
					class="font-weight-regular text-caption"
					@click="
						showData = true;
						dataType = 'graph-data';
					"
				>
					<template v-slot:prepend>
						<i class="fa-duotone fa-binary mr-3"></i>
					</template>
					View graph data
				</v-list-item>
				<v-list-item
					v-if="userCanCreateSegments"
					class="font-weight-regular text-caption"
					@click="isCreateSegmentDialogVisible = true"
				>
					<template v-slot:prepend>
						<i class="fa-duotone fa-object-intersect mr-3"></i>
					</template>
					Create segment
				</v-list-item>
				<v-list-item
					@click="isShareLinkDialogVisible = true"
					class="font-weight-regular text-caption"
				>
					<template v-slot:prepend>
						<i class="fa: fa-solid fa-link mr-3"></i>
					</template>
					Create share link
				</v-list-item>
			</v-list>
		</v-menu>
	</div>
</template>
